import { HttpParams } from '@angular/common/http'
import { Component, effect, input, output } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { RouterLink } from '@angular/router'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'

import { BaseComponent, Grid, normalizePartsFluidsItemKey, PartsFluidsCatalogItem, PartsFluidsItem } from '../../shared'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'

@Component({
    selector: 'app-parts-fluids-by-item',
    templateUrl: './parts-fluids-by-item.component.html',
    styleUrls: ['./parts-fluids-lookup.component.css'],
    standalone: true,
    imports: [FormsModule, RouterLink, WjGridModule, WjGridFilterModule, ActionToolbarCount, ActionToolbarSearchComponent, AutoSizeDirective],
})
export class PartsFluidsByItemComponent extends BaseComponent {
    selectedItem = input('')
    isActive = input(false)
    selectedItemChange = output<string>()
    selectedEquipmentChange = output<string>()

    catalogGrid: Grid<PartsFluidsCatalogItem>
    equipmentGrid: Grid<PartsFluidsItem>
    private applyingSelection = false

    constructor() {
        super()
        this.catalogGrid = new Grid<PartsFluidsCatalogItem>(this.services, 'itemKey', this)
            .isReadOnly(true)
            .wrapColumnHeaders()
            .doInitialSelection(false)
            .filterBy(item => [item.itemValue, item.description, item.categories, item.sources])
        this.catalogGrid.selectionChanged.on(item => this.onCatalogSelection(item))
        this.catalogGrid.initialized.on(() => this.selectCatalogRow(this.selectedItem()))

        this.equipmentGrid = new Grid<PartsFluidsItem>(this.services)
            .isReadOnly(true)
            .wrapColumnHeaders()
            .doInitialSelection(false)
            .filterBy(item => [
                item.equipment,
                item.equipmentDescription,
                item.source,
                item.category,
                item.subCategory,
                item.itemLabel,
                item.details,
            ])

        this.loadCatalog()

        effect(() => {
            const item = this.selectedItem()
            this.selectCatalogRow(item)
            this.loadEquipmentForItem(item)
        })

        effect(() => {
            if (!this.isActive()) return
            this.selectCatalogRow(this.selectedItem())
        })
    }

    loadCatalog() {
        this.cancelableRequest(
            'catalog',
            true,
            this.services.data.get<PartsFluidsCatalogItem[]>('api/EquipmentPartsFluidsLookup/Catalog').subscribe({
                next: result => {
                    this.initialLoadingComplete()
                    this.catalogGrid.items = result ?? []
                    this.selectCatalogRow(this.selectedItem())
                },
            }),
        )
    }

    loadEquipmentForItem(item: string) {
        if (!item) {
            this.equipmentGrid.items = []
            return
        }

        const params = new HttpParams().set('item', item)
        this.cancelableRequest(
            'forItem',
            true,
            this.services.data.get<PartsFluidsItem[]>('api/EquipmentPartsFluidsLookup/ForItem', params).subscribe({
                next: result => {
                    this.equipmentGrid.items = result ?? []
                },
            }),
        )
    }

    onCatalogSelection(item: PartsFluidsCatalogItem | null) {
        if (this.applyingSelection || !item?.itemValue) return
        if (normalizePartsFluidsItemKey(item.itemValue) === normalizePartsFluidsItemKey(this.selectedItem())) return
        this.selectedItemChange.emit(item.itemValue)
    }

    private selectCatalogRow(item: string) {
        const key = normalizePartsFluidsItemKey(item)
        if (!key) return

        const match = (this.catalogGrid.view.sourceCollection as PartsFluidsCatalogItem[] | undefined)?.find(x => x.itemKey === key)
        if (!match) return

        if (this.catalogGrid.filter && !this.catalogGrid.view.items.includes(match)) {
            this.catalogGrid.filter = ''
        }

        this.applyingSelection = true
        this.catalogGrid.selectedItem = match
        this.scrollCatalogRowIntoView(match)
        setTimeout(() => {
            this.scrollCatalogRowIntoView(match)
            this.applyingSelection = false
        }, 50)
    }

    private scrollCatalogRowIntoView(match: PartsFluidsCatalogItem) {
        const flex = this.catalogGrid.flexGrid
        if (!flex) return

        const row = this.catalogGrid.view.items.indexOf(match)
        if (row < 0) return

        flex.select(row, 0)
        flex.scrollIntoView(row, 0)
    }
}
